import { map, uniq } from 'lodash/fp';
import { useState } from 'react';
import { getLanguageOptions } from '@mews-commander/legacy-entities/src/ducks/languages';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { Option } from '@mews-commander/types';
import { Select } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Container, Size, SpacingToken, Stack } from '@optimus-web/core';
import { LanguageContext } from './LanguageContext';

interface Props {
    fill?: boolean;
    spacing?: SpacingToken;
    padded?: boolean;
    languageSelectSize?: Size;
    initialLanguageCode?: string;
    enableAllOption?: boolean;
}

const ALL_LANGUAGES_CODE = 'ALL';

export const FormLanguageProvider = ({
    children,
    fill,
    spacing = '700',
    padded = true,
    languageSelectSize,
    initialLanguageCode,
    enableAllOption = true,
}: React.PropsWithChildren<Props>) => {
    const defaultLanguageCode: string = useCommanderSelector(
        (state) => initialLanguageCode ?? getCurrentEnterpriseDefaultLanguageCode(state)
    );
    const languageOptions = useCommanderSelector(getLanguageOptions);

    const [languageCode, onChange] = useState(defaultLanguageCode);

    const languages =
        languageCode === ALL_LANGUAGES_CODE ? map('value', languageOptions) : uniq([defaultLanguageCode, languageCode]);

    const options = enableAllOption
        ? [
              {
                  value: ALL_LANGUAGES_CODE,
                  label: <LocalizedText textKey={TextKey.All} />,
              } as Option,
              ...languageOptions,
          ]
        : languageOptions;

    const languageSelectProps = {
        onChange,
        options,
        selected: languageCode,
        label: <LocalizedText textKey={TextKey.AddTranslation} />,
        bottomCaption: <LocalizedText textKey={TextKey.AddTranslationHint} />,
        size: languageSelectSize,
    };

    const content = (
        <Stack spacing={spacing} fill={fill} vertical>
            <Select {...languageSelectProps} name="localization" />
            <LanguageContext.Provider value={languages}>{children}</LanguageContext.Provider>
        </Stack>
    );

    return padded ? <Container padding={['300', '0']}>{content}</Container> : content;
};
